/**
 * [Iter 420 — 2026-03-01] Reusable "Link to Project" chip.
 *
 * Drop this into any entity detail page (Job Card, Picking List,
 * Procurement Tracker) to give the user a single, consistent affordance
 * for linking the entity to a project (or seeing the current link).
 *
 * Props:
 *   kind        — one of "jobcard" | "picking_list" | "procurement_tracker"
 *   entityId    — the id of the entity being linked
 *   projectId   — currently-linked project id (or null)
 *   customerId  — used to scope the project picker to this customer
 *   projectShortId, projectTitle — optional labels to render on the chip
 *   onLinked, onUnlinked — optional callbacks (parent should re-load)
 *
 * Uses the existing project link endpoints:
 *   POST   /api/projects/{pid}/link           body {kind, id}
 *   DELETE /api/projects/{pid}/link/{kind}/{eid}
 */
import { useEffect, useState } from "react";
import { Link as RLink } from "react-router-dom";
import { FolderOpen, Link2, X, Loader2, Search } from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";

export default function ProjectLinkChip({
  kind,
  entityId,
  projectId,
  customerId,
  projectShortId,
  projectTitle,
  onLinked,
  onUnlinked,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const openPicker = async () => {
    setPickerOpen(true);
    if (projects.length > 0) return;
    setLoading(true);
    try {
      const { data } = await api.get("/projects", {
        params: customerId ? { customer_id: customerId, limit: 100 } : { limit: 50 },
      });
      const items = Array.isArray(data) ? data : (data?.items || []);
      setProjects(items);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const link = async (pid) => {
    setBusy(true);
    try {
      await api.post(`/projects/${pid}/link`, { kind, id: entityId });
      toast.success("Linked to project.");
      setPickerOpen(false);
      onLinked && onLinked(pid);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed.");
    } finally { setBusy(false); }
  };

  const unlink = async () => {
    if (!projectId) return;
    if (!window.confirm("Unlink from this project?")) return;
    setBusy(true);
    try {
      await api.delete(`/projects/${projectId}/link/${kind}/${entityId}`);
      toast.success("Unlinked.");
      onUnlinked && onUnlinked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed.");
    } finally { setBusy(false); }
  };

  const filtered = q
    ? projects.filter((p) => {
        const hay = `${p.title || ""} ${p.name || ""} ${p.short_id || ""} ${p.id || ""}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      })
    : projects;

  // Already-linked chip
  if (projectId) {
    return (
      <div
        className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border border-[#A78BFA]/40 bg-[#A78BFA]/10 text-[#A78BFA]"
        data-testid="entity-project-chip"
      >
        <RLink
          to={`/projects/${projectId}`}
          className="inline-flex items-center gap-1 hover:underline"
        >
          <FolderOpen className="w-3 h-3" />
          <span className="font-mono">{projectShortId || projectId.slice(0, 8)}</span>
          {projectTitle && <span className="text-white/90">· {projectTitle}</span>}
        </RLink>
        <button
          type="button"
          onClick={unlink}
          disabled={busy}
          className="ml-1 hover:text-rose-300 disabled:opacity-50"
          title="Unlink from project"
          data-testid="entity-project-unlink"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // Not linked — show picker button
  return (
    <div className="relative inline-block" data-testid="entity-project-link-widget">
      <button
        type="button"
        onClick={openPicker}
        disabled={busy}
        className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border border-white/10 text-[#a0aabf] hover:text-white hover:border-[#A78BFA]/50 disabled:opacity-50"
        data-testid="entity-project-link-open"
      >
        <Link2 className="w-3 h-3" />
        Link to project
      </button>
      {pickerOpen && (
        <div
          className="absolute z-50 mt-1 left-0 w-72 max-h-72 overflow-y-auto rounded-md border border-white/10 bg-[#0a0b0e] shadow-xl p-2 space-y-1"
          data-testid="entity-project-list"
        >
          <div className="flex items-center justify-between text-[10px] text-[#647087] uppercase tracking-wider px-1 pb-1 border-b border-white/5">
            <span>Select project</span>
            <button
              type="button"
              onClick={() => setPickerOpen(false)}
              className="text-[#647087] hover:text-white"
              data-testid="entity-project-picker-close"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[#647087]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search projects…"
              className="hv-input !pl-6 !py-1 text-[11px] w-full"
              data-testid="entity-project-picker-search"
            />
          </div>
          {loading ? (
            <div className="text-[11px] text-[#647087] flex items-center gap-1 px-1 py-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-[11px] text-[#647087] italic px-1 py-2">
              {customerId ? "No projects for this customer." : "No projects found."}
            </div>
          ) : (
            filtered.slice(0, 30).map((p) => (
              <button
                key={p.id}
                type="button"
                disabled={busy}
                onClick={() => link(p.id)}
                className="w-full text-left text-[11px] px-2 py-1 rounded hover:bg-white/[0.06] text-white disabled:opacity-50"
                data-testid={`entity-project-pick-${p.id}`}
              >
                <span className="font-mono text-[#A78BFA]">{p.short_id || p.id.slice(0, 8)}</span>
                {" "}
                {p.title || p.name || ""}
                {p.status && <span className="text-[9px] text-[#647087] ml-1">· {p.status}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
